"use client";

import { LucideIcon } from "lucide-react";
import Reveal from "@/app/components/Reveal";

type ScrollStyle = {
  opacity: number;
  y: number;
  scale: number;
  filter: string;
};

type Props = {
  item: { icon: LucideIcon; title: string; text: string };
  scrollStyle?: ScrollStyle;
  index?: number;
  compact?: boolean;
};

export default function BenefitCard({ item, scrollStyle, index, compact }: Props) {
  const Icon = item.icon;

  if (scrollStyle) {
    return (
      <div
        style={{
          opacity: scrollStyle.opacity,
          transform: `translateY(${scrollStyle.y}px) scale(${scrollStyle.scale})`,
          filter: scrollStyle.filter,
        }}
        className="group relative w-full p-3 border border-white/10 bg-white/4 backdrop-blur-xl transition-all duration-500 hover:border-sky-400/40"
      >
        <CardContent Icon={Icon} item={item} />
      </div>
    );
  }

  return (
    <Reveal
      y={20}
      scale={0.95}
      duration={0.4}
      delay={(index ?? 0) * 0.06}
      className={`group relative w-full border border-white/10 bg-white/4 backdrop-blur-xl transition-all duration-500 hover:border-sky-400/40 ${
        compact ? "p-2.5" : "p-3"
      }`}
    >
      <CardContent Icon={Icon} item={item} compact={compact} />
    </Reveal>
  );
}

function CardContent({
  Icon,
  item,
  compact,
}: {
  Icon: LucideIcon;
  item: { title: string; text: string };
  compact?: boolean;
}) {
  return (
    <>
      <div
        className={`inline-flex items-center justify-center bg-sky-500/10 text-sky-400 mb-1 transition group-hover:shadow-[0_0_20px_rgba(14,165,233,.3)] ${
          compact ? "w-6 h-6" : "w-8 h-8 mb-2"
        }`}
      >
        <Icon size={compact ? 13 : 16} />
      </div>
      <h3
        className={`font-bold group-hover:text-sky-400 transition-colors ${
          compact ? "text-[13px] leading-snug mb-0.5" : "text-sm mb-1"
        }`}
      >
        {item.title}
      </h3>
      <p
        className={`text-white/60 ${
          compact ? "text-[11px] leading-snug line-clamp-3" : "text-xs leading-relaxed"
        }`}
      >
        {item.text}
      </p>
    </>
  );
}
